import type { Id } from "@packages/backend/convex/_generated/dataModel";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePaginatedQuery, useQuery } from "convex/react";
import { api } from "@packages/backend/convex/_generated/api";
import { useOutletContext, useSearchParams, Link } from "react-router-dom";
import { PlayerContext } from "../../App";
import map from "./mapData.json";
import "./explorer.css";
const colors: Record<string, string> = map.colors;
const text = (s = "") => {
  const node = document.createElement("div");
  node.innerHTML = s;
  return node.textContent || "";
};
const year = (s: string) => {
  const match = s.trim().match(/^(-?\d+)(?:\s*(BCE?|CE|AD))?/i);
  if (!match) return null;
  const n = Number(match[1]);
  return /^BC/i.test(match[2] || "") ? -Math.abs(n) : n;
};
const fmt = (n: number) =>
  n < 0 ? `${Math.abs(n)} BCE` : `${n} CE`;
const aliases: Record<string, string[]> = {
  "British Isles": [
    "britain",
    "england",
    "scotland",
    "wales",
    "ireland",
    "london",
  ],
  "Western & Central Europe": [
    "france",
    "germany",
    "italy",
    "spain",
    "portugal",
    "paris",
    "rome",
  ],
  "East Asia": ["china", "japan", "korea", "mongolia"],
  "South Asia": ["india", "pakistan", "bangladesh", "sri lanka"],
  "Northern America": ["united states", "canada", "america"],
  "Levant & Mesopotamia": ["iraq", "syria", "israel", "baghdad", "mesopotamia"],
  "North Africa": ["egypt", "libya", "morocco", "algeria"],
  Andes: ["peru", "bolivia", "chile", "ecuador"],
  Mesoamerica: ["mexico", "guatemala"],
  "Iranian Plateau": ["iran", "persia", "afghanistan"],
  "Anatolia & Caucasus": ["turkey", "anatolia", "armenia", "georgia"],
  "Pacific Islands": ["polynesia", "micronesia", "melanesia"],
  "Australia & New Zealand": ["australia", "new zealand"],
};
const historyCategories = [
  { label: "History", value: "PODCASTSERIES_HISTORY" },
  { label: "Music History", value: "PODCASTSERIES_MUSIC_HISTORY" },
  { label: "TV & Film History", value: "PODCASTSERIES_TV_AND_FILM_HISTORY" },
] as const;
// The import uses 201 for podcasts outside the current top-200 chart.
const chartRank = (rank?: number) =>
  rank !== undefined && Number.isInteger(rank) && rank > 0 && rank <= 200
    ? rank : Infinity;
type PlaylistEpisode = {
  _id: Id<"episode">; podcast_id: Id<"podcast">; title: string;
  podcast: string; places: string; first: number; last: number; hasAudio: boolean;
};
type SavedPlaylist = { id: string; title: string; episodes: PlaylistEpisode[] };
type PlaylistLibrary = { version: 1; activeId: string; playlists: SavedPlaylist[] };
const playlistStorageKey = "evrwhz.playlists.v1";
function readPlaylistLibrary(): PlaylistLibrary {
  try {
    const data = JSON.parse(localStorage.getItem(playlistStorageKey) || "null");
    if (data?.version === 1 && Array.isArray(data.playlists) && data.playlists.length &&
      data.playlists.every((p: SavedPlaylist) => typeof p.id === "string" && typeof p.title === "string" &&
        Array.isArray(p.episodes) && p.episodes.every(e => typeof e._id === "string" &&
          typeof e.podcast_id === "string" && typeof e.title === "string" && typeof e.podcast === "string" &&
          typeof e.places === "string" && Number.isFinite(e.first) && Number.isFinite(e.last) && typeof e.hasAudio === "boolean")) &&
      data.playlists.some((p: SavedPlaylist) => p.id === data.activeId)) return data;
  } catch { /* An unavailable or invalid saved library starts with an empty playlist. */ }
  return { version: 1, activeId: "default", playlists: [{ id: "default", title: "My playlist", episodes: [] }] };
}
export default function Explorer() {
  const [playlistLibrary, setPlaylistLibrary] = useState<PlaylistLibrary>(readPlaylistLibrary);
  const [playlistSaveError, setPlaylistSaveError] = useState(false);
  const playlist = playlistLibrary.playlists.find(p => p.id === playlistLibrary.activeId)!;
  const playlistIds = useMemo(() => new Set(playlist.episodes.map(e => e._id)), [playlist.episodes]);
  useEffect(() => {
    try { localStorage.setItem(playlistStorageKey, JSON.stringify(playlistLibrary)); setPlaylistSaveError(false); }
    catch { setPlaylistSaveError(true); }
  }, [playlistLibrary]);
  const updatePlaylist = (update: (p: SavedPlaylist) => SavedPlaylist) =>
    setPlaylistLibrary(library => ({ ...library, playlists: library.playlists.map(p => p.id === library.activeId ? update(p) : p) }));
  const addToPlaylist = (episode: PlaylistEpisode) => updatePlaylist(p => p.episodes.some(e => e._id === episode._id) ? p : ({
    ...p, episodes: [...p.episodes, {
      _id: episode._id, podcast_id: episode.podcast_id, title: episode.title,
      podcast: episode.podcast, places: episode.places, first: episode.first,
      last: episode.last, hasAudio: episode.hasAudio,
    }],
  }));
  const movePlaylistEpisode = (id: string, direction: number) => updatePlaylist(p => {
    const episodes = [...p.episodes], from = episodes.findIndex(e => e._id === id), to = from + direction;
    if (from < 0 || to < 0 || to >= episodes.length) return p;
    [episodes[from], episodes[to]] = [episodes[to], episodes[from]];
    return { ...p, episodes };
  });
  const episodeSection = useRef<HTMLElement>(null);
  const [episodePage, setEpisodePage] = useState(1);
  const [openDetails, setOpenDetails] = useState<string[]>([]);
  const [params, setParams] = useSearchParams();
  const indexParam = params.get("index");
  const index = indexParam === null ? null : Number(indexParam);
  const linkedRows = useQuery(api.page_timeline.pageOfTimeline,
    index !== null && Number.isInteger(index) && index >= 0
      ? { page: index + 1, pageSize: 1 } : "skip");
  const pods = useQuery(api.everwhz.podcasts, {});
  const { set_player_episode_id } = useOutletContext<PlayerContext>();
  const [categories, setCategories] = useState<string[]>(historyCategories.map(c => c.value));
  const allCategories = categories.length === historyCategories.length;
  const categoryPodcasts = (pods || []).filter(p => allCategories || categories.includes(p.chart || ""));
  const categoryPodcastIds = useMemo(() => new Set<string>(
    (pods || []).filter(p => categories.length === historyCategories.length || categories.includes(p.chart || "")).map(p => p._id),
  ), [pods, categories]);
  const [regions, setRegions] = useState<string[]>([]),
    [excluded, setExcluded] = useState<string[]>([]);
  const [start, setStart] = useState(-10000),
    [end, setEnd] = useState(2026),
    [query, setQuery] = useState(""),
    [podQuery, setPodQuery] = useState(""),
    [view, setView] = useState("Map"),
    [sort, setSort] = useState("date-desc"),
    [mobile, setMobile] = useState(false);
  useEffect(() => {
    const value = linkedRows?.[0] && year(linkedRows[0].start);
    if (value !== undefined && value !== null) {
      setStart(Math.max(-10000, Math.min(2026, value)));
      setEnd(2026);
    }
  }, [linkedRows]);
  const toggle = (name: string) =>
    setRegions(prev => prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]);
  const { results: raw, status, loadMore } = usePaginatedQuery(
    api.page_timeline.episodeIndex, {}, { initialNumItems: 250 },
  );
  useEffect(() => {
    if (status === "CanLoadMore") loadMore(250);
  }, [status, loadMore]);
  const loading = status === "LoadingFirstPage";
  const indexing = status !== "Exhausted";
  const podcastTitles = useMemo(() => new Map((pods || []).map(p => [p._id, p.title])), [pods]);
  const regionTerms = useMemo(() => Object.fromEntries(Object.keys(colors).map(region => [region,
    [...(aliases[region] || []), ...map.countries.filter(c => c.region === region).map(c => c.name.toLowerCase())],
  ])), []);
  type IndexRecord = (typeof raw)[number];
  type IndexedEpisode = IndexRecord & {
    description: string; podcast: string; places: string; tags: string[];
    first: number; last: number; hasDate: boolean;
  };
  const processedIndex = useRef(new Map<string, {
    source: IndexRecord; podcastTitle: string; episode: IndexedEpisode;
  }>());
  const episodes = useMemo(() => {
    const result: IndexedEpisode[] = [];
    for (const e of raw || []) {
      if (!e.years.length) continue;
      const podcastTitle = podcastTitles.get(e.podcast_id) || e.podcast_title;
      const cached = processedIndex.current.get(e._id);
      if (cached?.source === e && cached.podcastTitle === podcastTitle) {
        result.push(cached.episode);
        continue;
      }
        const dates = (e.years || [])
          .map(year)
          .filter((n): n is number => n !== null);
        const places = [...(e.geonames || []), ...(e.location || [])].join(
          " · ",
        );
        const hay = places.toLowerCase();
        const tags = Object.keys(colors).filter(
          (r) =>
            hay.includes(r.toLowerCase()) ||
            regionTerms[r].some(term => hay.includes(term)),
        );
        const episode = {
          ...e,
          title: text(e.title),
          description: "",
          podcast: text(podcastTitle),
          places,
          tags,
          first: Math.min(...dates),
          last: Math.max(...dates),
          hasDate: dates.length > 0,
        };
      processedIndex.current.set(e._id, { source: e, podcastTitle, episode });
      result.push(episode);
    }
    return result;
  }, [raw, podcastTitles, regionTerms]);
  const filtered = useMemo(
    () =>
      episodes
        .filter(
          (e) =>
            e.hasDate &&
            e.first <= end &&
            e.last >= start &&
            !excluded.includes(e.podcast_id) &&
            categoryPodcastIds.has(e.podcast_id) &&
            (regions.length === 0 || e.tags.some((r) => regions.includes(r))) &&
            `${e.title} ${e.description} ${e.podcast} ${e.places}`
              .toLowerCase()
              .includes(query.toLowerCase()),
        )
        .sort((a, b) =>
          sort === "title"
            ? a.title.localeCompare(b.title)
            : sort === "date-asc"
              ? a.first - b.first
              : b.first - a.first,
        ),
    [episodes, start, end, excluded, regions, query, sort, categoryPodcastIds],
  );
  const pageSize = 50;
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(episodePage, pageCount);
  const pageStart = (currentPage - 1) * pageSize;
  useEffect(() => {
    setEpisodePage(1);
    setOpenDetails([]);
  }, [start, end, excluded, regions, query, sort, categories]);
  const goToPage = (page: number) => {
    setEpisodePage(Math.max(1, Math.min(page, pageCount)));
    setOpenDetails([]);
    episodeSection.current?.scrollIntoView({ block: "start" });
  };
  const pagination = (position: string) => filtered.length > 0 && (
    <nav className="episode-pagination" aria-label={`Episode pages ${position}`}>
      <span aria-live="polite">
        {pageStart + 1}–{Math.min(pageStart + pageSize, filtered.length)} of {filtered.length.toLocaleString()}{indexing ? "+" : ""}
      </span>
      <div className="episode-page-buttons">
        <button className="top-btn" disabled={currentPage === 1} onClick={() => goToPage(1)} aria-label="First page">«</button>
        <button className="top-btn" disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)}>Previous</button>
        <span>{indexing ? `Page ${currentPage} · counting pages…` : `Page ${currentPage} of ${pageCount}`}</span>
        <button className="top-btn" disabled={currentPage === pageCount} onClick={() => goToPage(currentPage + 1)}>Next</button>
        <button className="top-btn" disabled={currentPage === pageCount} onClick={() => goToPage(pageCount)} aria-label={indexing ? "Last loaded page" : "Last page"}>»</button>
      </div>
    </nav>
  );
  const windowFrom = (a: number, b: number) => {
    setParams({}, { replace: true });
    setStart(a);
    setEnd(b);
  };
  const windowTo = (b: number) => {
    setParams({}, { replace: true });
    setEnd(b);
  };
  const reset = () => {
    setParams({}, { replace: true });
    setRegions([]);
    setExcluded([]);
    setStart(-10000);
    setEnd(2026);
    setQuery("");
    setCategories(historyCategories.map(c => c.value));
  };
  const sources = categoryPodcasts.filter((p) =>
    text(p.title).toLowerCase().includes(podQuery.toLowerCase()),
  );
  return (
    <div className={"layout explorer " + (mobile ? "show-filters" : "")}>
      <aside className="sidebar left">
        <div className="side-head">
          <span>Regions</span>
          <button className="text-btn" onClick={() => setRegions([])}>
            Clear
          </button>
        </div>
        <div className="side-scroll">
          {map.groups.map((g) => (
            <div key={g.group}>
              <div className="group-title">{g.group}</div>
              {g.items.map((item) => {
                const name = String(item[0]);
                return (
                  <button
                    key={name}
                    className={
                      "region " + (regions.includes(name) ? "active" : "")
                    }
                    aria-pressed={regions.includes(name)}
                    onClick={() => toggle(name)}
                  >
                    <span
                      className="dot"
                      style={{ background: colors[name] }}
                    />
                    <span className="region-name">{name}</span>
                    <span className="count">
                      {episodes.filter((e) => e.tags.includes(name)).length}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <p className="side-note">
          Regions inferred from place metadata. Counts reflect the episode index.
        </p>
      </aside>
      <main className="center">
        <div className="center-head">
          <div>
            <div className="eyebrow">Explore the archive</div>
            <h1 className="headline">History through podcasts</h1>
            <div className="subline">
              Browse by the history covered, across time and place.
            </div>
          </div>
          <div className="view-tabs">
            {["Map", "Episodes", "Playlist"].map((v) => (
              <button
                key={v}
                className={view === v ? "active" : ""}
                onClick={() => setView(v)}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
        <div className="explorer-search">
          <button
            className="top-btn mobile-filter"
            onClick={() => setMobile(!mobile)}
            aria-expanded={mobile}
          >
            Filters
          </button>
        </div>
        {playlistSaveError && view !== "Playlist" && <p role="alert" className="playlist-note">Playlist changes could not be saved in this browser.</p>}
        {view === "Map" && (
          <section className="map-wrap">
            <svg
              className="world"
              viewBox="0 0 1000 500"
              role="group"
              aria-label="Select a country to filter by historical region"
            >
              <defs>
                <pattern
                  id="grid"
                  width="78"
                  height="78"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 78 0 L 0 0 0 78"
                    fill="none"
                    stroke="#263443"
                    strokeWidth=".7"
                  />
                </pattern>
              </defs>
              <rect width="1000" height="500" fill="url(#grid)" />
              {map.countries.map((c) => (
                <path
                  key={c.id}
                  d={c.path}
                  className="land"
                  style={{
                    fill: colors[c.region] || "#1c3042",
                    opacity:
                      regions.length && !regions.includes(c.region) ? 0.3 : 1,
                  }}
                  role={c.region ? "button" : undefined}
                  tabIndex={c.region ? 0 : undefined}
                  aria-label={`${c.name} — ${c.region || "Unmapped"}`}
                  aria-pressed={regions.includes(c.region)}
                  onClick={() => c.region && toggle(c.region)}
                  onKeyDown={(e) => {
                    if (c.region && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      if (!e.repeat) toggle(c.region);
                    }
                  }}
                >
                  <title>
                    {c.name} · {c.region}
                  </title>
                </path>
              ))}
            </svg>
            <div className="map-foot">
              <span>
                <strong>{regions.length || "All"} regions</strong> selected
              </span>
              <span>Click a country to filter</span>
            </div>
          </section>
        )}
        {view !== "Playlist" && <section className="timeline">
          <div className="timeline-top">
            <div className="time-title">Time period covered</div>
            <div className="year-range">
              {fmt(start)} — {fmt(end)}
            </div>
          </div>
          <div className="rangebox">
            <div className="track" />
            <div
              className="track-fill"
              style={{
                left: ((start + 10000) / 12026) * 100 + "%",
                width: ((end - start) / 12026) * 100 + "%",
              }}
            />
            <input
              aria-label="Timeline start"
              type="range"
              min="-10000"
              max="2026"
              value={start}
              onChange={(e) =>
                windowFrom(Math.min(Number(e.target.value), end), end)
              }
            />
            <input
              aria-label="Timeline end"
              type="range"
              min="-10000"
              max="2026"
              value={end}
              onChange={(e) =>
                windowTo(Math.max(Number(e.target.value), start))
              }
            />
          </div>
          <div className="ticks">
            <span>10000 BCE</span>
            <span>1 CE</span>
            <span>2026</span>
          </div>
          <div className="active-filters">
            {regions.map((r) => (
              <span className="chip" key={r}>
                {r}
                <button aria-label={"Remove " + r} onClick={() => toggle(r)}>
                  ×
                </button>
              </span>
            ))}
            <button className="text-btn" onClick={reset}>
              Reset filters
            </button>
          </div>
        </section>}
        {view !== "Playlist" && <section className="episode-section" ref={episodeSection}>
          <div className="episode-heading">
            <div>
              <h2>
                Episodes in this view ({filtered.length}{indexing ? "+" : ""})
              </h2>
              <p>
                Dates refer to history covered. Filters apply to the episodes loaded so far.
                {indexing ? ` Loading the episode index… ${raw.length.toLocaleString()} records received.` : " The complete archive is loaded."}
              </p>
            </div>
            <select
              className="sort-select"
              aria-label="Sort episodes"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="date-desc">Newest history first</option>
              <option value="date-asc">Oldest history first</option>
              <option value="title">Title A–Z</option>
            </select>
          </div>
          {pagination("above results")}
          <div className="episodes">
            {loading || pods === undefined ? (
              <div className="empty" role="status">
                Loading the archive…
              </div>
            ) : filtered.length ? (
              filtered.slice(pageStart, pageStart + pageSize).map((e) => (
                <article className="episode" key={e._id}>
                  <div className="ep-top">
                    <Link to={`/episodes/${e.podcast_id}`}>
                      {e.podcast || "Untitled podcast"}
                    </Link>
                  </div>
                  <div className="ep-date">
                    {fmt(e.first)}
                    {e.last !== e.first ? " — " + fmt(e.last) : ""}
                  </div>
                  <h3 className="ep-title">{e.title || "Untitled episode"}</h3>
                  <div className="ep-bottom">
                    <span className="ep-region">
                      {e.places || "Place not annotated"}
                    </span>
                    <button
                      className="ep-link"
                      disabled={!e.hasAudio}
                      onClick={() => set_player_episode_id(e._id)}
                    >
                      {e.hasAudio ? "▶ Play episode" : "Audio unavailable"}
                    </button>
                    <button className="ep-link" aria-pressed={playlistIds.has(e._id)}
                      aria-label={playlistIds.has(e._id) ? `Remove ${e.title} from playlist` : `Add ${e.title} to playlist`}
                      onClick={() => playlistIds.has(e._id)
                        ? updatePlaylist(p => ({ ...p, episodes: p.episodes.filter(item => item._id !== e._id) }))
                        : addToPlaylist(e)}>
                      {playlistIds.has(e._id) ? "✓ Added" : "+ Playlist"}
                    </button>
                  </div>
                  <details onToggle={event => { const open = event.currentTarget.open; setOpenDetails(prev => open ? [...new Set([...prev, e._id])] : prev.filter(id => id !== e._id)); }}>
                    <summary>Episode details</summary>
                    {openDetails.includes(e._id) && <EpisodeDescription id={e._id} />}
                  </details>
                </article>
              ))
            ) : (
              <div className="empty">
                {indexing ? "Looking for matches while the full episode index loads…" : "No episodes match these filters."}
                <br />
                <button className="text-btn" onClick={reset}>
                  Reset filters
                </button>
              </div>
            )}
          </div>
          {pagination("below results")}

        </section>}
        {view === "Playlist" && (
          <section className="episode-section playlist-section" aria-label="Playlist">
            <div className="playlist-title-row">
              <label className="playlist-title-label" htmlFor="playlist-title">Playlist title</label>
              <input id="playlist-title" className="playlist-title" value={playlist.title} maxLength={120}
                onChange={event => updatePlaylist(p => ({ ...p, title: event.target.value }))}
                onBlur={() => updatePlaylist(p => ({ ...p, title: p.title.trim() || "My playlist" }))} />
              <span aria-live="polite">{playlist.episodes.length} episodes</span>
            </div>
            <p className="playlist-note">Saved in this browser. Add episodes from Map or Episodes. Use the arrows to change their order.</p>
            {playlistSaveError && <p role="alert">Playlist changes could not be saved in this browser. Keep this tab open to retain your changes.</p>}
            {playlist.episodes.length ? (
              <ol className="playlist-episodes">
                {playlist.episodes.map((e, index) => (
                  <li className="episode playlist-episode" key={e._id}>
                    <div className="playlist-position">{index + 1}</div>
                    <div className="playlist-episode-body">
                      <div className="ep-top"><Link to={`/episodes/${e.podcast_id}`}>{e.podcast || "Untitled podcast"}</Link></div>
                      <h3 className="ep-title">{e.title || "Untitled episode"}</h3>
                      <div className="ep-date">{fmt(e.first)}{e.first !== e.last ? ` — ${fmt(e.last)}` : ""}</div>
                      <div className="playlist-episode-actions">
                        <button className="ep-link" disabled={!e.hasAudio} onClick={() => set_player_episode_id(e._id)}>{e.hasAudio ? "▶ Play episode" : "Audio unavailable"}</button>
                        <button className="top-btn" disabled={index === 0} aria-label={`Move ${e.title} up`} onClick={() => movePlaylistEpisode(e._id, -1)}>↑</button>
                        <button className="top-btn" disabled={index === playlist.episodes.length - 1} aria-label={`Move ${e.title} down`} onClick={() => movePlaylistEpisode(e._id, 1)}>↓</button>
                        <button className="text-btn" aria-label={`Remove ${e.title} from playlist`} onClick={() => updatePlaylist(p => ({ ...p, episodes: p.episodes.filter(item => item._id !== e._id) }))}>Remove</button>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="empty">Your playlist is empty.<br /><button className="text-btn" onClick={() => setView("Episodes")}>Browse episodes</button></div>
            )}
          </section>
        )}
      </main>
      <aside className="sidebar right">
        <div className="side-head">
          <span>Podcasts</span>
          <span>{categoryPodcasts.length} sources</span>
        </div>
        <div className="presets podcast-categories" role="group" aria-label="History categories">
          {historyCategories.map(({ label, value }) => (
            <button key={value} className={"preset " + (categories.includes(value) ? "active" : "")}
              aria-pressed={categories.includes(value)}
              onClick={() => setCategories(current => current.includes(value)
                ? current.filter(c => c !== value) : [...current, value])}>
              {label}
            </button>
          ))}
        </div>
        <div className="podcast-search">
          <input
            aria-label="Filter podcasts"
            placeholder="Filter podcasts…"
            value={podQuery}
            onChange={(e) => setPodQuery(e.target.value)}
          />
        </div>
        <div className="podcast-actions">
          <span>{categoryPodcasts.filter(p => !excluded.includes(p._id)).length} selected</span>
          <button
            className="text-btn"
            onClick={() =>
              setExcluded(prev => categoryPodcasts.every(p => !prev.includes(p._id))
                ? [...new Set([...prev, ...categoryPodcasts.map(p => p._id)])]
                : prev.filter(id => !categoryPodcastIds.has(id)))
            }
          >
            {categoryPodcasts.every(p => !excluded.includes(p._id)) ? "Deselect all" : "Select all"}
          </button>
        </div>
        <div className="podcast-list">
          {[...historyCategories, { label: "Uncategorized", value: "" }].map(({ label, value }) => {
            const group = sources.filter(p => value ? p.chart === value
              : !historyCategories.some(c => c.value === p.chart))
              .sort((a, b) => {
                const rankA = chartRank(a.rank), rankB = chartRank(b.rank);
                return rankA === rankB ? text(a.title).localeCompare(text(b.title)) : rankA - rankB;
              });
            if (!group.length) return null;
            return (
              <details className="podcast-group" key={value} open>
                <summary>{label}<span>{group.length}</span></summary>
                {group.map(p => (
                  <label className="podcast" key={p._id}>
                    <input type="checkbox" checked={!excluded.includes(p._id)}
                      onChange={() => setExcluded(prev => prev.includes(p._id)
                        ? prev.filter(id => id !== p._id) : [...prev, p._id])} />
                    <span className="podcast-rank" aria-label={chartRank(p.rank) === Infinity ? "Unranked" : `Chart rank ${p.rank}`}>
                      {chartRank(p.rank) === Infinity ? "Unranked" : `#${p.rank}`}
                    </span>
                    <span className="podcast-name">{text(p.title) || "Untitled podcast"}</span>
                    <span className="podcast-count" title="Episodes">{p.number_of_episodes ?? "—"}</span>
                  </label>
                ))}
              </details>
            );
          })}
        </div>
      </aside>
    </div>
  );
}

function EpisodeDescription({ id }: { id: import("@packages/backend/convex/_generated/dataModel").Id<"episode"> }) {
  const episode = useQuery(api.everwhz.episode, { id });
  return <p>{episode === undefined ? "Loading details…" : text(episode?.episode_description) || "No description available."}</p>;
}
